import React, { useState, useEffect } from "react";
import styles from "styles/card.module.scss";
import Timer from "../Timer";
import { handleKeyDown } from "Utils/handleKeyDown";
import { ClientImages } from "contexts/CommonContext";

interface CardProps {
  responseData: any;
  clientImages: ClientImages | null;
  clientName: string;
  handleResendOtpClick: () => void;
  handleCancelClick: () => void;
  handleSubmit: (e: any) => void;
  handleOobSubmit: () => void;
  notification: string;
  notificationType: "success" | "error";
  updateNotification: (message: string) => void;
  isResendOtpClicked: boolean;
  isResendLimitAvailable: boolean;
  enableBiometric: boolean;
  setEnableBiometric: (val: boolean) => void;
}

const Card: React.FC<CardProps> = ({
  notification,
  notificationType,
  isResendOtpClicked,
  responseData,
  clientImages,
  clientName,
  handleCancelClick,
  handleResendOtpClick,
  handleSubmit,
  handleOobSubmit,
  updateNotification,
  isResendLimitAvailable,
  enableBiometric,
  setEnableBiometric,
}) => {
  const [resendDisabled, setResendDisabled] = useState(false);

  useEffect(() => {
    if (!isResendOtpClicked) setResendDisabled(false);
  }, [isResendOtpClicked]);

  const {
    mobileNumber,
    email,
    maskedCardNumber,
    ipAddress,
    transactionTime,
    timeout,
    resendOTPTime,
    amount,
    merchantName,
    currencyCode,
    challengeType,
    challengeInfoText,
    spcSupported,
  } = responseData || {};

  const [timeRemaining, setTimeRemaining] = useState(timeout);

  useEffect(() => {
    setTimeRemaining(timeout);
  }, [timeout]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const timerId = setInterval(() => {
        setTimeRemaining((prev: number) => prev - 1);
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [timeRemaining]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  if (clientImages) {
    document.title = clientName;
    const link = document.querySelector("link[rel~='icon']");
    if (link) link.setAttribute("href", clientImages.faviconUrl);
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.topBar}>
          {clientImages && (
            <>
              <img
                src={clientImages.logoUrl}
                alt={`${clientName} Logo`}
                className="issuer-logo"
              />
              <img
                src={clientImages.networkLogoUrl}
                alt="Card Network Logo"
                className="card-logo"
              />
            </>
          )}
        </div>

        {/* OTP challenge */}
        {challengeType !== 6 && (
          <div className={styles.contentContainer}>
            <div className={styles.contentBox}>
              {merchantName && (
                <div>
                  <span>Merchant</span>
                  <span>{merchantName}</span>
                </div>
              )}
              {amount && (
                <div>
                  <span>Amount</span>
                  <span>
                    {currencyCode} {amount}
                  </span>
                </div>
              )}
              <div>
                <span>Card number</span>
                <span>{maskedCardNumber}</span>
              </div>
            </div>
            <div className={styles.msg}>
              Please enter the One Time Password (OTP) sent to your registered
              {mobileNumber && <> mobile number <span>{mobileNumber}</span></>}
              {mobileNumber && email && " &"}
              {email && <> email <span>{email}</span></>}.
            </div>
            {notification && (
              <div
                className={`${styles.otpWarningSection} ${
                  notificationType === "error" ? styles.otpErrorSection : ""
                }`}
              >
                <div>
                  <span>{notification}</span>
                  <button
                    type="button"
                    className={styles.dismissBtn}
                    onClick={() => updateNotification("")}
                    aria-label="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              </div>
            )}
            <div className={`${styles.msg} ${styles.darkNote}`}>
              Note: The OTP page will expire in:{" "}
              <span>{formatTime(timeRemaining)}</span> seconds.
            </div>
            <form onSubmit={handleSubmit}>
              <div className={styles.inputContainer}>
                <label htmlFor="otp" className="otp-label">
                  Enter One Time Password (OTP)
                </label>
                <input
                  type="password"
                  pattern="\d*"
                  maxLength={6}
                  inputMode="numeric"
                  autoFocus
                  id="otp"
                  name="otp"
                  placeholder="Enter OTP"
                  onKeyDown={(e) => handleKeyDown(e, updateNotification)}
                />
                <div className={styles.resendCancelRow}>
                  <div className={styles.cancelButtonSection}>
                    <button type="button" onClick={handleCancelClick}>
                      Cancel
                    </button>
                  </div>
                  {isResendLimitAvailable && (
                    <div className={styles.resendOtpSection}>
                      {isResendOtpClicked ? (
                        <div>
                          Resend OTP in{" "}
                          <span>
                            <Timer resendOTPTime={resendOTPTime} />
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="text-btn"
                          onClick={() => {
                            setResendDisabled(true);
                            handleResendOtpClick();
                          }}
                          disabled={resendDisabled}
                        >
                          Resend OTP
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {spcSupported && (
                  <label
                    className={`${styles.biometricToggle} ${
                      enableBiometric ? styles.biometricToggleEnabled : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={enableBiometric}
                      onChange={(e) => setEnableBiometric(e.target.checked)}
                      className={styles.biometricCheckbox}
                    />
                    <div className={styles.biometricTextWrap}>
                      <div className={styles.biometricTitle}>Enable Biometric Authentication</div>
                      <div className={styles.biometricSubtitle}>
                        Use fingerprint or Face ID to authenticate future transactions without OTP.
                      </div>
                    </div>
                  </label>
                )}

                <button type="submit" className={styles.submitBtn}>
                  Submit
                </button>
              </div>
            </form>
          </div>
        )}

        {/* OOB challenge */}
        {challengeType === 6 && (
          <div className={styles.contentContainer}>
            <form onSubmit={handleOobSubmit}>
              <div className={styles.inputContainer}>
                <div className={styles.oobInstructions}>
                  <h3>Follow these steps:</h3>
                  <ul>
                    {challengeInfoText &&
                      challengeInfoText.split(":").map((step: string, idx: number) => (
                        <li key={idx}>{step.trim()}</li>
                      ))}
                  </ul>
                </div>
                <div className={styles.cancelButtonSection}>
                  <button type="button" onClick={handleCancelClick}>
                    Cancel
                  </button>
                </div>
                <button type="submit" className={styles.submitBtn}>
                  Continue
                </button>
              </div>
            </form>
          </div>
        )}

        <div className={styles.textBorder}></div>
        <div className={styles.txnMsg}>
          Your transaction is processed through a secure 128-bit https internet
          connection based on secure socket layer technology. For security
          purpose, your IP address {ipAddress} and access time {transactionTime}{" "}
          have been logged.
        </div>
      </div>
      <div className={styles.note}>
        <span>Note:</span> Please do not press the back button or exit the
        app/website while we complete the transaction. This page will
        automatically timeout after {timeout} seconds.
      </div>
    </div>
  );
};

export default Card;
